import { SVGProps } from 'react';
import ReactDOMServer from 'react-dom/server';

import { CompositeLayer } from '@deck.gl/core';
import { PathLayer, ScatterplotLayer, TextLayer, IconLayer } from '@deck.gl/layers';

import { Position } from '../../types/Geometry';
import TakeOffIcon from './icons/TakeOffIcon.svg?react';
import { MapItem, MapItemType } from './MapItem';

const svgToDataUrl = (SvgComp: React.FC<SVGProps<SVGSVGElement>>, props = {}) => {
  const svgString = ReactDOMServer.renderToStaticMarkup(<SvgComp width={24} height={24} {...props} />);
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
};

interface WaypointsLayerProps {
  takeOffPoint?: Position;
  waypoints: Position[];
  selectedItemIndex?: number;
  hoveredItem?: MapItem;
  onItemSelected?: (item: MapItem) => void;
  onItemDrag?: (item: MapItem) => void;
  onDragStateChange?: (isDragging: boolean) => void;
  onItemHover?: (item: MapItem | undefined) => void;
}

class WaypointsAndOrbitsLayer extends CompositeLayer<WaypointsLayerProps> {
  static layerName = 'WaypointsAndOrbitsLayer';

  hoveredWaypointColor = [100, 180, 255, 255] as [number, number, number, number];
  selectedWaypointOutlineColor = [255, 0, 0, 255] as [number, number, number, number];
  waypointColor = [40, 130, 255, 255] as [number, number, number, number];

  state = {
    draggedItem: undefined as MapItem | undefined
  };

  updateState({ changeFlags }: any) {
    if (changeFlags.stateChanged) {
      this.setNeedsRedraw();
    }
  }

  onHover(info: any, event: any) {
    const hoveredItem = info.object as MapItem;
    this.props.onItemHover?.(hoveredItem || null);
    return true;
  }

  onClick(info: any, event: any) {
    const selectedItem = info.object as MapItem;

    if (this.props.onItemSelected && selectedItem) {
      this.props.onItemSelected(selectedItem);
    }

    return true;
  }

  onDragStart(info: any, event: any) {
    const draggedItem = info.object as MapItem;
    this.setState({ draggedItem });
    this.props.onDragStateChange?.(true);
    return true;
  }

  onDrag(info: any, event: any) {
    if (!this.state.draggedItem || !this.props.onItemDrag) return true;

    const newPosition = {
      ...this.state.draggedItem.position,
      longitude: info.coordinate[0],
      latitude: info.coordinate[1]
    };

    this.props.onItemDrag({
      type: this.state.draggedItem.type,
      index: this.state.draggedItem.index,
      position: newPosition
    });

    return true;
  }

  onDragEnd(info: any, event: any) {
    this.setState({ draggedItem: undefined });

    // Notify parent to re-enable map controller
    if (this.props.onDragStateChange) {
      this.props.onDragStateChange(false);
    }

    return true;
  }

  renderLayers() {
    const connectingLinesLayer = new PathLayer({
      id: 'waypoint-path',
      data: [
        {
          path: [
            this.props.takeOffPoint ? [this.props.takeOffPoint.longitude, this.props.takeOffPoint.latitude] : undefined,
            ...this.props.waypoints.map((waypoint) => [waypoint.longitude, waypoint.latitude])
          ].filter(Boolean)
        }
      ],
      getPath: (d) => d.path,
      widthUnits: 'pixels',
      getWidth: 3,
      getColor: [0, 255, 255],
      rounded: true
    });

    const dotsAtWaypointsLayer = new ScatterplotLayer({
      id: 'waypoint-dots',
      data: this.props.waypoints.map((waypointPosition, index) => ({
        position: waypointPosition,
        index: index + 2,
        type: MapItemType.Waypoint
      })),
      getPosition: (d) => [d.position.longitude, d.position.latitude],
      radiusUnits: 'pixels',
      getRadius: 18,
      getFillColor: (d) => {
        const isHovered =
          this.props.hoveredItem?.type === MapItemType.Waypoint && this.props.hoveredItem?.index === d.index;
        return isHovered ? this.hoveredWaypointColor : this.waypointColor;
      },
      getLineColor: [255, 255, 255, 255],
      lineWidthUnits: 'pixels',
      getLineWidth: 2,
      pickable: true
    });

    const waypointNumbersLayer = new TextLayer({
      id: 'waypoint-labels',
      data: this.props.waypoints.map((waypointPosition, index) => ({
        ...waypointPosition,
        index: index + 2
      })),
      getPosition: (d) => [d.longitude, d.latitude],
      getText: (d) => d.index.toString(),
      getSize: 24,
      sizeUnits: 'pixels',
      getColor: [255, 255, 255, 255],
      getBackgroundColor: [0, 0, 0, 0],
      background: true,
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center'
    });

    const takeOffCircleLayer = new ScatterplotLayer({
      id: 'takeoff-circle',
      data: this.props.takeOffPoint
        ? [
            {
              position: this.props.takeOffPoint,
              index: 1,
              type: MapItemType.Takeoff
            }
          ]
        : [],
      getPosition: (d) => [d.position.longitude, d.position.latitude],
      radiusUnits: 'pixels',
      getRadius: 18,
      getFillColor: (d) => {
        const isHovered = this.props.hoveredItem?.type === MapItemType.Takeoff;
        return isHovered ? this.hoveredWaypointColor : this.waypointColor;
      },
      getLineColor: [255, 255, 255, 255],
      lineWidthUnits: 'pixels',
      getLineWidth: 2,
      pickable: true
    });

    const takeOffIconLayer = new IconLayer({
      id: 'takeoff-icon',
      data: this.props.takeOffPoint ? [this.props.takeOffPoint] : [],
      getPosition: (d) => [d.longitude, d.latitude],
      getIcon: () => ({
        url: svgToDataUrl(TakeOffIcon, {
          width: 24,
          height: 24,
          fill: '#ff4444',
          stroke: '#ffffff',
          strokeWidth: 4
        }),
        width: 24,
        height: 24,
        anchorX: 12,
        anchorY: 14,
        mask: false
      }),
      getSize: 32,
      sizeUnits: 'pixels'
    });

    const getSelectedItemPosition = () => {
      if (this.props.selectedItemIndex === undefined) {
        return [];
      }

      if (this.props.selectedItemIndex === 1 && this.props.takeOffPoint) {
        return [this.props.takeOffPoint];
      }

      if (this.props.selectedItemIndex >= 2 && this.props.waypoints[this.props.selectedItemIndex - 2]) {
        return [this.props.waypoints[this.props.selectedItemIndex - 2]];
      }

      return [];
    };

    const selectionHighlightLayer = new ScatterplotLayer({
      id: 'selection-highlight',
      data: getSelectedItemPosition(),
      getPosition: (d) => [d.longitude, d.latitude],
      radiusUnits: 'pixels',
      getRadius: 20,
      getFillColor: [0, 0, 0, 0],
      getLineColor: this.selectedWaypointOutlineColor,
      lineWidthUnits: 'pixels',
      getLineWidth: 4,
      pickable: false,
      stroked: true
    });

    return [
      connectingLinesLayer,
      dotsAtWaypointsLayer,
      waypointNumbersLayer,
      takeOffCircleLayer,
      takeOffIconLayer,
      selectionHighlightLayer
    ];
  }
}

export default WaypointsAndOrbitsLayer;
